import React, { useCallback, useMemo } from 'react';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { VIEW_BY_TYPE } from '@constants/index';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBaselineComparisonSlice,
  setPeriodicOverviewFilters
} from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import { TextWithTooltipIcon } from '@components';
import { getGeneralSlice } from '@store/features';
import { useBaselineComp } from '@hooks/useBaselineComp';
import { BaselineComparisonChart } from '@components/Charts';
import DataTypeSelect from '@components/Inputs/DataTypeSelect';
import LookupSelect from '@components/Inputs/LookupSelect';
import { LOOKUP_OPERATIONS } from '@lookups';
import HourDaySelect from '../../../../Inputs/HourDaySelect';
import { useBaselineQueries } from '@hooks';
import PeriodOverviewKpis from '@components/Sections/BaselineComparisonSection/SubSections/PeriodicOverview/PeriodOverviewKpis';

function PeriodicOverviewComparison({
  allEquipments
}) {
  const { getPeriodicOverviewSeries } = useBaselineComp();
  const dispatch = useDispatch();
  const { displayUnit } = useSelector(getGeneralSlice);

  const { baselineOverviewQueries } = useBaselineQueries();
  const mainData = baselineOverviewQueries[0].data;
  const baselineData = baselineOverviewQueries[1].data;
  const {
    periodicOverview: { dataType, operation, chartType, viewBy }
  } = useSelector(getBaselineComparisonSlice);

  const handleChange = useCallback((val) => {
    dispatch(setPeriodicOverviewFilters(val));
  }, []);

  const allCategoryOptions = useMemo(() => {
    if(!allEquipments?.length) return [];

    let equipmentTypes = [...new Set(allEquipments.map(c => c.equipmentType))];
    return equipmentTypes.map((equipmentType, index) => ({
      value: index, 
      name: equipmentType, 
      key: equipmentType === 'Hotel and utilities' ? 'Accom' : equipmentType 
    }));
  }, [allEquipments]);

  const selectedCategoryData = useMemo(() => {
    if (operation === null || operation === undefined) 
      return allCategoryOptions;
    if (operation.length === 0 || !allCategoryOptions?.length) 
      return [];

    return operation.filter((sel) => allCategoryOptions.find((opt) => opt.value === sel.value));
  }, [operation, allCategoryOptions]);

  const loading = baselineOverviewQueries?.some((item) => item.isFetching);

  const {
    mainSeries = [],
    baseSeries = [],
    categories = [],
    unit,
    max = 0
  } = useMemo(() => {
    if (!mainData || !baselineData || !selectedCategoryData?.length) return {};
    return getPeriodicOverviewSeries(mainData, baselineData, selectedCategoryData);
  }, [displayUnit, mainData, baselineData, selectedCategoryData, dataType, viewBy]);

  const selectedCategoryValue = useMemo(() => {
    if (!allCategoryOptions?.length) 
      return [];
    if (operation === null || operation === undefined) 
      return allCategoryOptions;

    return operation.filter((sel) => allCategoryOptions.find((opt) => opt.value === sel.value));
  }, [operation, allCategoryOptions]);
   
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack justifyContent={'space-between'} direction={'row'}>
          <Typography variant={'h6'}>Periodic overview comparison</Typography>
        </Stack>
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Insights comparing to the Baseline
        </Typography>
        <PeriodOverviewKpis loading={loading} baselineData={baselineData} mainData={mainData} />
        <Typography variant={'subtitle1'} bold color={'text.secondary'}>
          Comparison over time
        </Typography>
        <BaselineComparisonChart
          dataType={dataType}
          chartType={chartType}
          viewBy={viewBy === VIEW_BY_TYPE.DAILY ? 'day' : 'hour'}
          chartActions={[
            <LookupSelect
              loading={!allCategoryOptions?.length}
              options={allCategoryOptions || []}
              label={'System'}
              key={'operations-select'}
              value={selectedCategoryValue}
              onChange={(val) => {
                handleChange({ operation: val });
              }}
            />,

            <DataTypeSelect
              key={'data-type-select'}
              value={dataType}
              onChange={(val) => {
                handleChange({ dataType: val });
              }}
            />,
            <HourDaySelect
              key={'view-by-select'}
              value={viewBy}
              handleChange={(val) => {
                handleChange({ viewBy: val });
              }}
            />
          ]}
          yAxisTitle={unit}
          mainMax={max}
          loading={loading}
          mainSeries={mainSeries}
          baseSeries={baseSeries}
          categories={categories}
        />
      </Stack>
    </Paper>
  );
}

PeriodicOverviewComparison.propTypes = {};

PeriodicOverviewComparison.defaultProps = {};

export default PeriodicOverviewComparison;
